import React, { useMemo } from 'react';

/**
 * Filter component for BackfillModal file selection.
 * Provides filtering by file extension, path, and name.
 * 
 * @param {Array} files - Array of file objects with name, path properties
 * @param {Object} filters - Current filter state { extensions: Set, paths: Set, nameFilter: string }
 * @param {function} onFiltersChange - Callback when filters change
 */
export default function BackfillFilters({ files, filters, onFiltersChange }) {
    // Extract unique file extensions from files
    const availableExtensions = useMemo(() => {
        const extensionSet = new Set();
        files.forEach(file => {
            const name = file.name || '';
            const lastDot = name.lastIndexOf('.');
            if (lastDot > 0) {
                const ext = name.substring(lastDot + 1).toLowerCase();
                extensionSet.add(ext);
            }
        });
        return Array.from(extensionSet).sort();
    }, [files]);

    // Extract unique folder paths from files
    const availablePaths = useMemo(() => {
        const pathSet = new Set();
        files.forEach(file => {
            const folderPath = file.folderPath || file.path || '';
            if (folderPath) {
                pathSet.add(folderPath);
            }
        });
        return Array.from(pathSet).sort();
    }, [files]);

    // Find common path prefix to shorten display (stop one level before the deepest common folder to show client name)
    const commonPathPrefix = useMemo(() => {
        if (availablePaths.length === 0) return '';
        
        // Find the deepest common prefix among all paths
        let prefix = availablePaths[0];
        for (let i = 1; i < availablePaths.length; i++) {
            while (!availablePaths[i].startsWith(prefix) && prefix.length > 0) {
                // Trim to previous slash
                const lastSlash = prefix.lastIndexOf('/');
                prefix = lastSlash > 0 ? prefix.substring(0, lastSlash) : '';
            }
            if (prefix.length === 0) break;
        }
        
        // Go up one more level so the client folder name is always shown
        // e.g., "Client Documents/Clients R - Z/Test, Me" -> "Client Documents/Clients R - Z"
        if (prefix) {
            const lastSlash = prefix.lastIndexOf('/');
            if (lastSlash > 0) {
                prefix = prefix.substring(0, lastSlash);
            }
        }
        
        return prefix;
    }, [availablePaths]);

    // Helper to display shortened path
    const getDisplayPath = (path) => {
        if (commonPathPrefix && path.startsWith(commonPathPrefix)) {
            let remainder = path.substring(commonPathPrefix.length);
            // Remove leading slash if present
            if (remainder.startsWith('/')) {
                remainder = remainder.substring(1);
            }
            // If remainder is empty, this is the root folder - show the last folder name from prefix
            if (!remainder) {
                const trimmedPrefix = commonPathPrefix.endsWith('/') 
                    ? commonPathPrefix.slice(0, -1) 
                    : commonPathPrefix;
                const lastSlash = trimmedPrefix.lastIndexOf('/');
                const folderName = lastSlash >= 0 ? trimmedPrefix.substring(lastSlash + 1) : trimmedPrefix;
                return '.../' + folderName + '/*';
            }
            return '.../' + remainder + '/*';
        }
        return (path.length > 40 ? '...' + path.slice(-40) : path) + '/*';
    };

    // Helper to count files in a path (including subfolders)
    const getFileCountForPath = (targetPath) => {
        return files.filter(f => {
            const filePath = f.folderPath || f.path || '';
            // Match exact path or paths that start with targetPath/
            return filePath === targetPath || filePath.startsWith(targetPath + '/');
        }).length;
    };

    // Toggle extension selection
    const toggleExtension = (ext) => {
        const newExtensions = new Set(filters.extensions);
        if (newExtensions.has(ext)) {
            newExtensions.delete(ext);
        } else {
            newExtensions.add(ext);
        }
        onFiltersChange({ ...filters, extensions: newExtensions });
    };

    // Select all extensions
    const selectAllExtensions = () => {
        onFiltersChange({ ...filters, extensions: new Set(availableExtensions) });
    };

    // Clear all extensions
    const clearAllExtensions = () => {
        onFiltersChange({ ...filters, extensions: new Set() });
    };

    // Toggle path selection
    const togglePath = (path) => {
        const newPaths = new Set(filters.paths);
        if (newPaths.has(path)) {
            newPaths.delete(path);
        } else {
            newPaths.add(path);
        }
        onFiltersChange({ ...filters, paths: newPaths });
    };

    // Select all paths
    const selectAllPaths = () => {
        onFiltersChange({ ...filters, paths: new Set(availablePaths) });
    };

    // Clear all paths
    const clearAllPaths = () => {
        onFiltersChange({ ...filters, paths: new Set() });
    };

    // Update name filter
    const handleNameFilterChange = (value) => {
        onFiltersChange({ ...filters, nameFilter: value });
    };

    // Update KB filter
    const handleKbFilterChange = (value) => {
        onFiltersChange({ ...filters, kbFilter: value });
    };

    // Clear all filters
    const clearAllFilters = () => {
        onFiltersChange({
            extensions: new Set(),
            paths: new Set(),
            nameFilter: '',
            kbFilter: 'all'
        });
    };

    // Check if any filters are active
    const hasActiveFilters = filters.extensions.size > 0 || filters.paths.size > 0 || filters.nameFilter || filters.kbFilter !== 'all';

    return (
        <div className="card mb-3">
            <div className="card-header py-2 d-flex justify-content-between align-items-center">
                <span>
                    <i className="bi bi-funnel me-2"></i>
                    Filters
                </span>
                {hasActiveFilters && (
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-danger"
                        onClick={clearAllFilters}
                    >
                        <i className="bi bi-x-circle me-1"></i>
                        Clear All
                    </button>
                )}
            </div>
            <div className="card-body py-2">
                <div className="row g-2">
                    {/* Name Filter */}
                    <div className="col-md-2">
                        <label className="form-label small mb-1">
                            <i className="bi bi-file-text me-1"></i>
                            Name
                        </label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Filter by file name..."
                            value={filters.nameFilter}
                            onChange={(e) => handleNameFilterChange(e.target.value)}
                        />
                    </div>

                    {/* Path Filter */}
                    <div className="col-md-5">
                        <label className="form-label small mb-1">
                            <i className="bi bi-folder me-1"></i>
                            Paths
                            {filters.paths.size > 0 && (
                                <span className="badge bg-primary ms-1">{filters.paths.size}</span>
                            )}
                        </label>
                        <div className="dropdown">
                            <button
                                className="btn btn-outline-secondary btn-sm dropdown-toggle w-100 text-start"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                {filters.paths.size === 0
                                    ? 'All paths'
                                    : filters.paths.size === availablePaths.length
                                        ? 'All paths'
                                        : `${filters.paths.size} selected`}
                            </button>
                            <div className="dropdown-menu p-2" style={{ maxHeight: '250px', overflowY: 'auto', minWidth: '350px', zIndex: 1050 }}>
                                <div className="d-flex justify-content-between mb-2">
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0"
                                        onClick={selectAllPaths}
                                    >
                                        Select All
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0"
                                        onClick={clearAllPaths}
                                    >
                                        Clear
                                    </button>
                                </div>
                                <div className="dropdown-divider"></div>
                                {availablePaths.length === 0 ? (
                                    <span className="text-muted small">No paths found</span>
                                ) : (
                                    availablePaths.map(path => (
                                        <div key={path} className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={`path-${path}`}
                                                checked={filters.paths.has(path)}
                                                onChange={() => togglePath(path)}
                                            />
                                            <label className="form-check-label small" htmlFor={`path-${path}`} title={path}>
                                                {getDisplayPath(path)}
                                                <span className="text-muted ms-1">
                                                    ({getFileCountForPath(path)})
                                                </span>
                                            </label>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Extension Filter */}
                    <div className="col-md-3">
                        <label className="form-label small mb-1">
                            <i className="bi bi-file-earmark me-1"></i>
                            Extensions
                            {filters.extensions.size > 0 && (
                                <span className="badge bg-primary ms-1">{filters.extensions.size}</span>
                            )}
                        </label>
                        <div className="dropdown">
                            <button
                                className="btn btn-outline-secondary btn-sm dropdown-toggle w-100 text-start"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                {filters.extensions.size === 0
                                    ? 'All extensions'
                                    : filters.extensions.size === availableExtensions.length
                                        ? 'All extensions'
                                        : `${filters.extensions.size} selected`}
                            </button>
                            <div className="dropdown-menu p-2" style={{ maxHeight: '250px', overflowY: 'auto', minWidth: '200px', zIndex: 1050 }}>
                                <div className="d-flex justify-content-between mb-2">
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0"
                                        onClick={selectAllExtensions}
                                    >
                                        Select All
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0"
                                        onClick={clearAllExtensions}
                                    >
                                        Clear
                                    </button>
                                </div>
                                <div className="dropdown-divider"></div>
                                {availableExtensions.length === 0 ? (
                                    <span className="text-muted small">No extensions found</span>
                                ) : (
                                    availableExtensions.map(ext => (
                                        <div key={ext} className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={`ext-${ext}`}
                                                checked={filters.extensions.has(ext)}
                                                onChange={() => toggleExtension(ext)}
                                            />
                                            <label className="form-check-label" htmlFor={`ext-${ext}`}>
                                                .{ext}
                                                <span className="text-muted ms-1">
                                                    ({files.filter(f => {
                                                        const name = f.name || '';
                                                        const lastDot = name.lastIndexOf('.');
                                                        return lastDot > 0 && name.substring(lastDot + 1).toLowerCase() === ext;
                                                    }).length})
                                                </span>
                                            </label>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* KB Status Filter */}
                    <div className="col-md-2">
                        <label className="form-label small mb-1">
                            <i className="bi bi-database me-1"></i>
                            KB Status
                        </label>
                        <select
                            className="form-select form-select-sm"
                            value={filters.kbFilter || 'all'}
                            onChange={(e) => handleKbFilterChange(e.target.value)}
                        >
                            <option value="all">All Files</option>
                            <option value="in">In KB</option>
                            <option value="not">Not in KB</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
}
